import {
  useEffect,
  useRef,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from "react";
import type { DriveStep } from "driver.js";
import { useTranslation } from "react-i18next";
import { useDriver } from "@/hooks/useDriver";

const detailTourButtonAttr = "data-driver-detail-launch";

type UseDesktopTourOptions = {
  iconsContainerRef: RefObject<HTMLDivElement | null>;
  onIconWiggle: Dispatch<SetStateAction<number>>;
};

/**
 * Registriert die Desktop-Touren "desktop-intro" und
 * "desktop-intro-desktop-apps" (Driver.js). Die Tour-IDs werden von der
 * Taskbar via startTour referenziert.
 */
export function useDesktopTour({
  iconsContainerRef,
  onIconWiggle,
}: UseDesktopTourOptions) {
  const { t, i18n } = useTranslation();
  const { registerTour, startTour } = useDriver();
  const wiggleTimeoutsRef = useRef<number[]>([]);

  // Tour-Flags müssen in Refs sein, damit sie über useEffect Re-Runs hinweg persistent bleiben
  const tourFlagsRef = useRef({
    resumeMainAfterDetail: false,
  });

  useEffect(() => {
    const tourId = "desktop-intro";
    const detailTourId = "desktop-intro-desktop-apps";
    const wiggleClass = "driver-step-wiggle";

    const clearScheduledWiggles = () => {
      wiggleTimeoutsRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      wiggleTimeoutsRef.current = [];
      if (typeof document !== "undefined") {
        document.querySelectorAll(`.${wiggleClass}`).forEach((el) => {
          el.classList.remove(wiggleClass);
        });
      }
    };

    const triggerWiggle = (selectors: string | string[]) => {
      if (typeof document === "undefined") return;
      const list = Array.isArray(selectors) ? selectors : [selectors];
      list.forEach((selector) => {
        document.querySelectorAll(selector).forEach((element) => {
          element.classList.remove(wiggleClass);
          void (element as HTMLElement).offsetWidth;
          element.classList.add(wiggleClass);
          const timeoutId = window.setTimeout(() => {
            element.classList.remove(wiggleClass);
          }, 600);
          wiggleTimeoutsRef.current.push(timeoutId);
        });
      });
    };

    const buildDesktopDetailSteps = (): DriveStep[] => {
      const icons = Array.from(
        document.querySelectorAll<HTMLElement>("[data-driver-app-id]")
      );
      if (!icons.length) return [];

      return icons.map((element, index) => {
        const appId = element.getAttribute("data-driver-app-id") ?? "";
        const appLabel = t(`apps.${appId}`);
        const descriptionKey = `tour.desktopAppsDetail.descriptions.${appId}`;
        const description = t(descriptionKey, {
          defaultValue: t("tour.desktopAppsDetail.defaultDescription", {
            app: appLabel,
          }),
        });
        const rect = element.getBoundingClientRect();
        const prefersBottom = rect.top < window.innerHeight / 2;
        const prefersRight = rect.left > window.innerWidth * 0.6;
        const prefersLeft = rect.left < window.innerWidth * 0.4;
        const side = prefersBottom ? "bottom" : "top";
        const align = prefersLeft ? "start" : prefersRight ? "end" : "center";

        return {
          element: () => element,
          popover: {
            title: t("tour.desktopAppsDetail.title", { app: appLabel }),
            description,
            side,
            align,
            popoverClass: "cdb-driver-popover",
            showButtons: ["next", "previous", "close"],
            showProgress: true,
            progressText: t("tour.desktopAppsDetail.progressText", {
              current: index + 1,
              total: icons.length,
            }),
            nextBtnText: t("tour.desktopAppsDetail.next"),
            prevBtnText: t("tour.desktopAppsDetail.prev"),
            doneBtnText: t("tour.desktopAppsDetail.done"),
          },
          onHighlighted: () => {
            const innerElement = element.querySelector(
              '[data-driver-id="desktop-icon-inner"]'
            ) as HTMLElement | null;
            if (innerElement) {
              innerElement.classList.remove(wiggleClass);
              void innerElement.offsetWidth;
              innerElement.classList.add(wiggleClass);
            }
          },
          onDeselected: () => {
            const innerElement = element.querySelector(
              '[data-driver-id="desktop-icon-inner"]'
            );
            if (innerElement) {
              innerElement.classList.remove(wiggleClass);
            }
          },
        } satisfies DriveStep;
      });
    };

    const launchDesktopAppsDetailTour = () => {
      const steps = buildDesktopDetailSteps();
      if (!steps.length) return;
      clearScheduledWiggles();
      tourFlagsRef.current.resumeMainAfterDetail = true;
      startTour(detailTourId, {
        steps,
        config: {
          prevBtnText: t("tour.desktopAppsDetail.prev"),
          nextBtnText: t("tour.desktopAppsDetail.next"),
          doneBtnText: t("tour.desktopAppsDetail.done"),
          progressText: t("tour.desktopAppsDetail.progressText", {
            current: "{{current}}",
            total: "{{total}}",
          }),
          onDestroyed: () => {
            if (tourFlagsRef.current.resumeMainAfterDetail) {
              tourFlagsRef.current.resumeMainAfterDetail = false;
              setTimeout(() => {
                startTour(tourId, { stepIndex: 1 });
              }, 100);
            }
          },
        },
      });
    };

    const unregisterDetailTour = registerTour({
      id: detailTourId,
      steps: [],
    });

    const unregister = registerTour({
      id: tourId,
      steps: [
        {
          element: '[data-driver-id="start-menu-button"]',
          popover: {
            title: t("tour.desktopIntro.startMenu.title"),
            description: t("tour.desktopIntro.startMenu.description"),
            side: "top",
            align: "start",
          },
          onHighlighted: () => {
            triggerWiggle('[data-driver-id="start-menu-button"]');
          },
        },
        {
          element: () => {
            if (iconsContainerRef.current) {
              return iconsContainerRef.current;
            }
            const fallback = document.querySelector(
              '[data-driver-id="desktop-icons-area"]'
            );
            if (!fallback) {
              throw new Error("Desktop icons container not found");
            }
            return fallback;
          },
          popover: {
            title: t("tour.desktopIntro.desktop.title"),
            description: t("tour.desktopIntro.desktop.description"),
            side: "bottom",
            align: "center",
            popoverClass: "cdb-driver-popover",
            onPopoverRender: (popover) => {
              const { footerButtons } = popover;
              if (!footerButtons) return;
              const existing = footerButtons.querySelector(
                `[${detailTourButtonAttr}="true"]`
              );
              const label = t("tour.desktopIntro.desktop.detailButton");
              if (existing) {
                existing.textContent = label;
                return;
              }
              const detailButton = document.createElement("button");
              detailButton.className = "driver-detail-btn";
              detailButton.type = "button";
              detailButton.setAttribute(detailTourButtonAttr, "true");
              detailButton.textContent = label;
              detailButton.addEventListener("click", (event) => {
                event.preventDefault();
                event.stopPropagation();
                launchDesktopAppsDetailTour();
              });
              footerButtons.prepend(detailButton);
            },
          },
          onHighlighted: () => {
            onIconWiggle((prev) => prev + 1);
            triggerWiggle(
              '[data-driver-id="desktop-icons-area"] [data-driver-id="desktop-icon-inner"]'
            );
          },
        },
        {
          element: '[data-driver-id="taskbar-dock"]',
          popover: {
            title: t("tour.desktopIntro.taskbar.title"),
            description: t("tour.desktopIntro.taskbar.description"),
            side: "top",
            align: "center",
          },
          onHighlighted: () => {
            triggerWiggle('[data-driver-id="taskbar-dock"] .taskbar-button');
          },
        },
        {
          element: '[data-driver-id="taskbar-command-palette"]',
          popover: {
            title: t("tour.desktopIntro.commandPalette.title"),
            description: t("tour.desktopIntro.commandPalette.description"),
            side: "top",
            align: "end",
          },
          onHighlighted: () => {
            triggerWiggle('[data-driver-id="taskbar-command-palette"]');
          },
        },
        {
          element: '[data-driver-id="taskbar-notification-bell"]',
          popover: {
            title: t("tour.desktopIntro.notificationBell.title"),
            description: t("tour.desktopIntro.notificationBell.description"),
            side: "top",
            align: "end",
          },
          onHighlighted: () => {
            triggerWiggle('[data-driver-id="taskbar-notification-bell"]');
          },
        },
        {
          element: '[data-driver-id="taskbar-clock"]',
          popover: {
            title: t("tour.desktopIntro.widgets.title"),
            description: t("tour.desktopIntro.widgets.description"),
            side: "top",
            align: "end",
          },
          onHighlighted: () => {
            triggerWiggle('[data-driver-id="taskbar-clock"]');
          },
        },
        {
          element: '[data-driver-app-id="communicator2"]',
          popover: {
            title: t("tour.desktopIntro.cdbChat.title"),
            description: t("tour.desktopIntro.cdbChat.description"),
            side: "bottom",
            align: "center",
          },
        },
        {
          element: '[data-driver-id="desktop-tour-button"]',
          popover: {
            title: t("tour.desktopIntro.helpButton.title"),
            description: t("tour.desktopIntro.helpButton.description"),
            side: "top",
            align: "end",
          },
          onHighlighted: () => {
            triggerWiggle('[data-driver-id="desktop-tour-button"]');
          },
        },
      ],
      config: {
        prevBtnText: t("tour.controls.prev"),
        nextBtnText: t("tour.controls.next"),
        doneBtnText: t("tour.controls.done"),
        progressText: t("tour.controls.progress", {
          current: "{{current}}",
          total: "{{total}}",
        }),
      },
    });

    return () => {
      clearScheduledWiggles();
      // Flags NICHT hier zurücksetzen - sie werden in den Callbacks selbst zurückgesetzt
      // Wenn wir sie hier zurücksetzen, werden sie gelöscht bevor die onDestroyed Callbacks laufen können
      unregister();
      unregisterDetailTour();
    };
  }, [
    registerTour,
    startTour,
    onIconWiggle,
    iconsContainerRef,
    t,
    i18n.language,
  ]);
}
